import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Box, DraggableGrid, Tooltip, useDevice } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { CustomPageCard } from '~/components/CustomPageCard/CustomPageCard';
import { useLocaleKeys } from '~/i18n';
import { scrollIntoView } from '~/infra/utils';
import { MAX_CUSTOM_PAGES, reindexDisplayOrders, sortedPages } from '~/models/custom-page/model';
import { useAppConfig } from '~/store/store-context';
import { AddCustomPageDropdown } from './AddCustomPageDropdown/AddCustomPageDropdown';
import styles from './CustomPagesSection.module.scss';
export const GetCustomPagesSectionTID = (parentTestId)=>getTestIds(parentTestId);
function DraggableCustomPageCard(param) {
    let { renderCard, isDragActive, dragHandle } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: renderCard(isDragActive, dragHandle)
    });
}
const EMPTY_SET = new Set();
export const CustomPagesSection = (param)=>{
    let { testId, customPages, setCustomPages, isReadOnly, submitAttempted } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetCustomPagesSectionTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const { disableScrollAnimation } = useAppConfig();
    const sorted = useMemo(()=>sortedPages(customPages), [
        customPages
    ]);
    const isMaxReached = customPages.length >= MAX_CUSTOM_PAGES;
    const isDraggable = !isReadOnly && sorted.length >= 2;
    const [newlyAddedPageIds, setNewlyAddedPageIds] = useState(EMPTY_SET);
    const [_isDragging, setIsDragging] = useState(false);
    const cardRefs = useRef({});
    const handleAddPage = useCallback((page)=>{
        setCustomPages((prev)=>[
                ...prev,
                page
            ]);
        setNewlyAddedPageIds((prev)=>new Set(prev).add(page.id));
        requestAnimationFrame(()=>{
            const el = cardRefs.current[page.id];
            if (el != null) {
                scrollIntoView(el, {
                    disableScrollAnimation
                });
            }
            setNewlyAddedPageIds((prev)=>{
                const next = new Set(prev);
                next.delete(page.id);
                return next;
            });
        });
    }, [
        setCustomPages,
        disableScrollAnimation
    ]);
    const handleRemovePage = useCallback((pageId)=>{
        setCustomPages((prev)=>reindexDisplayOrders(prev.filter((p)=>p.id !== pageId)));
    }, [
        setCustomPages
    ]);
    const handlePageUpdate = useCallback((updatedPage)=>{
        setCustomPages((prev)=>prev.map((p)=>p.id === updatedPage.id ? updatedPage : p));
    }, [
        setCustomPages
    ]);
    const handleTitleChange = useCallback((pageId, newTitle)=>{
        setCustomPages((prev)=>prev.map((p)=>p.id === pageId ? _object_spread_props(_object_spread({}, p), {
                    pageTitle: newTitle
                }) : p));
    }, [
        setCustomPages
    ]);
    const handleDisplayScopeChange = useCallback((pageId, scope)=>{
        setCustomPages((prev)=>prev.map((p)=>p.id === pageId ? _object_spread_props(_object_spread({}, p), {
                    displayScope: scope
                }) : p));
    }, [
        setCustomPages
    ]);
    const handleReorder = useCallback((reorderedItems)=>{
        const reorderedPages = reorderedItems.map((item)=>sorted.find((p)=>p.id === item.id)).filter((p)=>p != null);
        setCustomPages(reindexDisplayOrders(reorderedPages));
    }, [
        sorted,
        setCustomPages
    ]);
    const renderPageCard = (page, isDragActive, dragHandle)=>/*#__PURE__*/ _jsx(Box, {
            ref: (el)=>cardRefs.current[page.id] = el,
            children: /*#__PURE__*/ _jsx(CustomPageCard, {
                page: page,
                isReadOnly: isReadOnly,
                initialExpanded: newlyAddedPageIds.has(page.id),
                submitAttempted: submitAttempted,
                onRemove: handleRemovePage,
                onPageUpdate: handlePageUpdate,
                onTitleChange: (newTitle)=>handleTitleChange(page.id, newTitle),
                onDisplayScopeChange: (scope)=>handleDisplayScopeChange(page.id, scope),
                isDragActive: isDragActive,
                dragHandle: dragHandle
            })
        });
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.CustomPagesSectionTID.Root(),
        className: cn(styles.root, {
            mobile: isMobileDevice
        }),
        children: [
            sorted.length > 0 && /*#__PURE__*/ _jsx(Box, {
                testId: testIds.CustomPagesSectionTID.CardList(),
                className: styles.cardList,
                children: isDraggable ? /*#__PURE__*/ _jsx("div", {
                    style: {
                        '--draggable-grid-item-radius': '8px'
                    },
                    children: /*#__PURE__*/ _jsx(DraggableGrid, {
                        items: sorted.map((page)=>({
                                id: page.id,
                                renderCard: (isDragActive, dragHandle)=>renderPageCard(page, isDragActive, dragHandle)
                            })),
                        itemComponent: DraggableCustomPageCard,
                        onReorder: handleReorder,
                        onDragStateChange: setIsDragging,
                        dragMode: "withGhost",
                        layout: {
                            columns: 1
                        },
                        itemDragActivation: "handle",
                        itemHandlePosition: "inside",
                        gap: 0,
                        testId: "custom-pages-grid"
                    })
                }) : sorted.map((page)=>renderPageCard(page))
            }),
            !isReadOnly && (isMaxReached ? /*#__PURE__*/ _jsx(Tooltip, {
                title: t.customPages.maxPagesReached(),
                children: /*#__PURE__*/ _jsx("span", {
                    style: {
                        display: 'block'
                    },
                    children: /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: styles.addPageSlot,
                        disabled: true,
                        children: [
                            /*#__PURE__*/ _jsx(PlusCircle, {
                                size: 16
                            }),
                            t.customPages.addCustomPage()
                        ]
                    })
                })
            }) : /*#__PURE__*/ _jsx(AddCustomPageDropdown, {
                testId: testId,
                nextDisplayOrder: customPages.length,
                onAddPage: handleAddPage
            }))
        ]
    });
};
